import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { useAdminControls } from "../AdminMode";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { ChatPushReminder } from "../components/ChatPushReminder";
import { Label } from "../components/ui/label";
import {
  MessageCircle, Send, Users, Calendar, Shield, Hash, Plus, Trash2,
  BellOff, Bell, Edit, MoreVertical, Lock, Info, Paperclip, SmilePlus,
  Reply, X, Check, Download, FileText, Image as ImageIcon, ArrowLeft,
  BarChart3, Search, Pin, PinOff, Gavel, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "../lib/api";

const POLL_MS = 4000;
const QUICK_REACTIONS = ["👍", "❤️", "😂", "🎉", "😮", "😢", "🔥", "👏"];

const roomIcon = (type) => {
  switch (type) {
    case "band": return <Users className="w-4 h-4" />;
    case "committee": return <Shield className="w-4 h-4" />;
    case "family": return <Hash className="w-4 h-4" />;
    case "section": return <Hash className="w-4 h-4" />;
    case "event": return <Calendar className="w-4 h-4" />;
    case "custom": return <MessageCircle className="w-4 h-4" />;
    default: return <MessageCircle className="w-4 h-4" />;
  }
};

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString();
};

export default function Chat() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { showAdminControls } = useAdminControls();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [activeRoomId, setActiveRoomId] = useState(searchParams.get("room") || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  // Phase 2 state
  const [replyTo, setReplyTo] = useState(null);         // {id, sender_name, body}
  const [editingId, setEditingId] = useState(null);     // message id being edited
  const [editBody, setEditBody] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState([]); // [{id, filename, content_type, size}]
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null); // {raw, start} or null
  const [mentionCandidates, setMentionCandidates] = useState([]);
  const [mentionIndex, setMentionIndex] = useState(0);
  const fileInputRef = useRef(null);

  // Phase 3 state
  const [pollOpen, setPollOpen] = useState(false);
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [pollMulti, setPollMulti] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [pinnedCollapsed, setPinnedCollapsed] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [potentialMembers, setPotentialMembers] = useState([]);
  const [newRoomName, setNewRoomName] = useState("");
  const [newRoomMembers, setNewRoomMembers] = useState([]);
  // Member name lookup for reaction tooltips ({user_id: "Display Name"})
  const [memberNamesById, setMemberNamesById] = useState({});
  // iter-106 — admin room members viewer
  const [membersOpen, setMembersOpen] = useState(false);
  const [membersData, setMembersData] = useState(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const scrollRef = useRef(null);
  const lastMsgIdRef = useRef(null);

  const activeRoom = rooms.find((r) => r.id === activeRoomId);

  // ----- Fetch rooms (polled) -----
  const fetchRooms = useCallback(async () => {
    try {
      const r = await apiClient.get("/chat/rooms");
      setRooms(r.data || []);
      // Auto-pick the first room only on desktop (mobile users should pick from the list)
      const isDesktop = typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches;
      if (!activeRoomId && isDesktop && r.data && r.data.length > 0) {
        const firstId = r.data[0].id;
        setActiveRoomId(firstId);
        setSearchParams({ room: firstId }, { replace: true });
      }
    } catch (e) {
      console.error("fetchRooms failed", e);
    } finally {
      setRoomsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoomId]);

  // ----- Fetch messages for active room -----
  const fetchMessages = useCallback(async (quiet = false) => {
    if (!activeRoomId) return;
    try {
      const r = await apiClient.get(`/chat/rooms/${activeRoomId}/messages?limit=50`);
      const msgs = r.data?.messages || [];
      setMessages(msgs);
      if (msgs.length > 0) lastMsgIdRef.current = msgs[msgs.length - 1].id;
      // Mark read
      if (!quiet) {
        try {
          await apiClient.post(`/chat/rooms/${activeRoomId}/read`);
        } catch {}
      }
    } catch (e) {
      if (!quiet) toast.error("Failed to load messages");
    }
  }, [activeRoomId]);

  // ----- Initial load -----
  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  // ----- Load member name lookup once (for reaction "who reacted" tooltips) -----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiClient.get("/chat/mention-candidates");
        if (cancelled) return;
        const map = {};
        (r.data || []).forEach((m) => {
          if (m && m.id) map[m.id] = m.name || m.email || "Unknown";
        });
        setMemberNamesById(map);
      } catch (e) {
        // Non-fatal — tooltip will fall back to user IDs
        console.warn("member name lookup failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ----- When active room changes, load messages + mark read -----
  useEffect(() => {
    if (!activeRoomId) return;
    fetchMessages(false);
    setSearchParams({ room: activeRoomId }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoomId]);

  // ----- Polling (pause when tab hidden) -----
  useEffect(() => {
    let mounted = true;
    const poll = async () => {
      if (document.hidden || !mounted) return;
      await fetchRooms();
      if (activeRoomId) await fetchMessages(true);
    };
    const iv = setInterval(poll, POLL_MS);
    const onVis = () => { if (!document.hidden) poll(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      mounted = false;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoomId]);

  // ----- Auto-scroll to bottom on new messages -----
  useEffect(() => {
    if (scrollRef.current) {
      // Use double rAF so the scroll happens AFTER the new message has
      // actually been laid out (otherwise scrollHeight is still the old
      // value and the last message stays clipped at the bottom).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          }
        });
      });
    }
  }, [messages.length, activeRoomId]);

  const sendMessage = async () => {
    const body = input.trim();
    if ((!body && pendingAttachments.length === 0) || !activeRoomId) return;
    setSending(true);
    try {
      const r = await apiClient.post(`/chat/rooms/${activeRoomId}/messages`, {
        body: body || "(attachment)",
        reply_to_id: replyTo?.id || null,
        attachment_ids: pendingAttachments.map((a) => a.id),
      });
      setInput("");
      setReplyTo(null);
      setPendingAttachments([]);
      setMessages((prev) => [...prev, { ...r.data }]);
      fetchRooms();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    // Mention autocomplete navigation
    if (mentionQuery && mentionCandidates.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionIndex((i) => (i + 1) % mentionCandidates.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionIndex((i) => (i - 1 + mentionCandidates.length) % mentionCandidates.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        applyMention(mentionCandidates[mentionIndex]);
        return;
      }
      if (e.key === "Escape") {
        setMentionQuery(null);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ---- @mention autocomplete ----
  const onInputChange = async (e) => {
    const val = e.target.value;
    setInput(val);
    // Detect trailing "@..." at caret. iter-107ab: allow bare `@` (zero
    // chars after) so the dropdown opens IMMEDIATELY when the user
    // hits `@`, matching WhatsApp / Slack behaviour. Previously the
    // regex required at least one letter (`[A-Za-z]`) before showing
    // anything, which felt broken — users typed `@` and the dropdown
    // stayed empty until they kept typing.
    const cursor = e.target.selectionStart ?? val.length;
    const before = val.slice(0, cursor);
    // The `@` must be at start of input OR preceded by whitespace, so
    // we don't trigger on email addresses or substrings.
    const m = before.match(/(?:^|\s)@([\w\-' ]{0,30})$/);
    if (m) {
      const query = m[1] || "";
      // mention starts at the position of the `@` — count back from cursor
      const start = cursor - query.length - 1;
      setMentionQuery({ raw: query, start });
      try {
        const r = await apiClient.get(`/chat/mention-candidates?q=${encodeURIComponent(query)}${activeRoomId ? `&room_id=${activeRoomId}` : ""}`);
        setMentionCandidates(r.data || []);
        setMentionIndex(0);
      } catch {
        setMentionCandidates([]);
      }
    } else {
      setMentionQuery(null);
      setMentionCandidates([]);
    }
  };

  const applyMention = (user) => {
    if (!mentionQuery || !user) return;
    const start = mentionQuery.start;
    const end = start + 1 + mentionQuery.raw.length; // "@" + text
    const needsQuotes = user.name.includes(" ");
    const replacement = needsQuotes ? `@"${user.name}" ` : `@${user.name} `;
    const nextInput = input.slice(0, start) + replacement + input.slice(end);
    setInput(nextInput);
    setMentionQuery(null);
    setMentionCandidates([]);
  };

  // ---- Reactions ----
  const toggleReaction = async (messageId, emoji) => {
    try {
      const r = await apiClient.post(`/chat/messages/${messageId}/reactions`, { emoji });
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, reactions: r.data.reactions } : m))
      );
    } catch {
      toast.error("Failed to react");
    }
  };

  // ---- Attachments ----
  const onFileSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (pendingAttachments.length + files.length > 4) {
      toast.error("Max 4 attachments per message");
      return;
    }
    setUploadingAttachment(true);
    try {
      for (const f of files) {
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`${f.name} is over 10MB`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        const r = await apiClient.post(`/chat/rooms/${activeRoomId}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPendingAttachments((prev) => [...prev, r.data]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingAttachment(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePendingAttachment = (id) => {
    setPendingAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  // ---- Edit message ----
  const startEdit = (m) => {
    setEditingId(m.id);
    setEditBody(m.body || "");
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditBody("");
  };
  const saveEdit = async () => {
    if (!editBody.trim()) return;
    try {
      await apiClient.put(`/chat/messages/${editingId}`, { body: editBody.trim() });
      setMessages((prev) =>
        prev.map((m) =>
          m.id === editingId
            ? { ...m, body: editBody.trim(), edited_at: new Date().toISOString() }
            : m
        )
      );
      cancelEdit();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to edit");
    }
  };

  // ---- Phase 3: polls ----
  const createPoll = async () => {
    const q = pollQuestion.trim();
    const opts = pollOptions.map((o) => o.trim()).filter(Boolean);
    if (!q || opts.length < 2) {
      toast.error("Question + at least 2 options required");
      return;
    }
    try {
      await apiClient.post(`/chat/rooms/${activeRoomId}/polls`, {
        question: q,
        options: opts,
        multi_choice: pollMulti,
      });
      setPollOpen(false);
      setPollQuestion("");
      setPollOptions(["", ""]);
      setPollMulti(false);
      fetchMessages(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create poll");
    }
  };

  const votePoll = async (messageId, optionIndices) => {
    try {
      const r = await apiClient.post(`/chat/messages/${messageId}/vote`, {
        option_indices: optionIndices,
      });
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, poll: r.data.poll } : m))
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to vote");
    }
  };

  const closePoll = async (messageId) => {
    if (!confirm("Close this poll? Nobody can vote after closing.")) return;
    try {
      await apiClient.post(`/chat/messages/${messageId}/close-poll`);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, poll: { ...(m.poll || {}), closed: true } } : m
        )
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to close poll");
    }
  };

  // ---- Phase 3: search ----
  const runSearch = async () => {
    const q = searchQ.trim();
    if (q.length < 2) { toast.error("At least 2 characters"); return; }
    try {
      const r = await apiClient.get(`/chat/search?q=${encodeURIComponent(q)}&room_id=${activeRoomId}`);
      setSearchResults(r.data.results || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed");
    }
  };


  // iter-106 — admin viewer: who can see this room?
  const openRoomMembers = async () => {
    if (!activeRoomId) return;
    setMembersOpen(true);
    setMembersLoading(true);
    setMembersData(null);
    try {
      const r = await apiClient.get(`/chat/rooms/${activeRoomId}/members`);
      setMembersData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load members");
      setMembersOpen(false);
    } finally {
      setMembersLoading(false);
    }
  };


  // ---- Phase 3: pin ----
  const togglePin = async (messageId) => {
    try {
      const r = await apiClient.post(`/chat/messages/${messageId}/pin`);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, pinned_at: r.data.pinned ? r.data.pinned_at : null } : m
        )
      );
      fetchPinned();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to pin");
    }
  };

  const fetchPinned = useCallback(async () => {
    if (!activeRoomId) { setPinnedMessages([]); return; }
    try {
      const r = await apiClient.get(`/chat/rooms/${activeRoomId}/pinned`);
      setPinnedMessages(r.data?.pinned || []);
    } catch {
      setPinnedMessages([]);
    }
  }, [activeRoomId]);

  useEffect(() => {
    if (activeRoomId) fetchPinned();
  }, [activeRoomId, fetchPinned]);

  // ---- Ratification (committee chat only) ----
  const [ratifyPanelOpen, setRatifyPanelOpen] = useState(false);
  const [ratifyTab, setRatifyTab] = useState("pending"); // pending | ratified
  const [ratifyItems, setRatifyItems] = useState([]);
  const [ratifyLoading, setRatifyLoading] = useState(false);

  const isCommitteeRoom = activeRoom?.type === "committee";

  const toggleRatifyFlag = async (messageId) => {
    let note = "";
    const m = messages.find((x) => x.id === messageId);
    const alreadyFlagged = m?.ratification && !m.ratification.ratified_at;
    if (!alreadyFlagged) {
      note = window.prompt("Optional note for the meeting (e.g. 'Approved £200 for stand'):", "") || "";
    }
    try {
      const r = await apiClient.post(`/chat/messages/${messageId}/ratify-flag`, { note });
      setMessages((prev) =>
        prev.map((x) =>
          x.id === messageId ? { ...x, ratification: r.data.flagged ? r.data.ratification : null } : x
        )
      );
      toast.success(r.data.flagged ? "Flagged for ratification" : "Flag removed");
      if (ratifyPanelOpen) fetchRatification();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to flag");
    }
  };

  const markRatified = async (messageId) => {
    const meetingDate = window.prompt("Meeting date (YYYY-MM-DD), or leave blank:", "") || "";
    try {
      await apiClient.post(`/chat/messages/${messageId}/ratify`, { meeting_date: meetingDate });
      toast.success("Marked as ratified");
      fetchRatification();
      // Refresh visible messages — use fetchMessages so the response is correctly
      // unwrapped ({messages: [...], room_id: ...}). Passing the raw dict to
      // setMessages caused a render crash + blank screen.
      fetchMessages(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mark ratified");
    }
  };

  const fetchRatification = useCallback(async () => {
    if (!activeRoomId || !isCommitteeRoom) return;
    setRatifyLoading(true);
    try {
      const r = await apiClient.get(`/chat/ratification?status=${ratifyTab}`);
      setRatifyItems(r.data?.items || []);
    } catch {
      setRatifyItems([]);
    } finally {
      setRatifyLoading(false);
    }
  }, [activeRoomId, isCommitteeRoom, ratifyTab]);

  useEffect(() => {
    if (ratifyPanelOpen) fetchRatification();
  }, [ratifyPanelOpen, ratifyTab, fetchRatification]);

  const downloadRatification = (format) => {
    const url = `${BACKEND_URL}/api/chat/ratification/export.${format}`;
    apiClient.get(`/chat/ratification/export.${format}`, { responseType: "blob" }).then((r) => {
      const blob = new Blob([r.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `ratification_${new Date().toISOString().slice(0, 10)}.${format}`;
      link.click();
      URL.revokeObjectURL(link.href);
    }).catch((e) => toast.error(e.response?.data?.detail || `Failed to export ${format}`));
  };

  const toggleMute = async () => {
    if (!activeRoom) return;
    try {
      const r = await apiClient.post(`/chat/rooms/${activeRoom.id}/mute`);
      toast.success(r.data.muted ? "Muted — you won't get push for this room" : "Unmuted");
      fetchRooms();
    } catch {
      toast.error("Failed to toggle mute");
    }
  };

  const deleteMessage = async (messageId) => {
    if (!confirm("Delete this message?")) return;
    try {
      await apiClient.delete(`/chat/messages/${messageId}`);
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, deleted: true, body: "" } : m))
      );
    } catch {
      toast.error("Failed to delete");
    }
  };

  const deleteRoom = async () => {
    if (!activeRoom || !["custom", "event"].includes(activeRoom.type)) return;
    if (!confirm(`Delete room "${activeRoom.name}"? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/chat/rooms/${activeRoom.id}`);
      toast.success("Room deleted");
      setActiveRoomId(null);
      fetchRooms();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const openCreate = async () => {
    try {
      const r = await apiClient.get("/chat/potential-members");
      setPotentialMembers(r.data || []);
      setNewRoomName("");
      setNewRoomMembers([]);
      setCreateOpen(true);
    } catch {
      toast.error("Failed to load members");
    }
  };

  const createRoom = async () => {
    if (!newRoomName.trim()) { toast.error("Name required"); return; }
    try {
      const r = await apiClient.post("/chat/rooms", {
        name: newRoomName.trim(),
        member_ids: newRoomMembers,
      });
      toast.success("Room created");
      setCreateOpen(false);
      setActiveRoomId(r.data.id);
      fetchRooms();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create room");
    }
  };

  return (
    <div
      className="flex flex-col chat-page-height"
      data-testid="chat-page-wrapper"
    >
      <ChatPushReminder />
      <div className="flex-1 flex flex-col md:flex-row gap-3 min-h-0" data-testid="chat-page">
      {/* Rooms sidebar — full width on mobile, hidden when a room is picked on mobile */}
      <aside
        className={`md:w-72 shrink-0 glass-card rounded-lg flex flex-col ${
          activeRoomId ? "hidden md:flex" : "flex flex-1 md:flex-none"
        }`}
        data-testid="chat-rooms-list"
      >
        <div className="p-3 border-b border-white/5 flex items-center justify-between">
          <h2 className="font-heading text-sm uppercase tracking-widest text-primary">Chats</h2>
          {showAdminControls && (
            <Button size="sm" variant="ghost" onClick={openCreate} data-testid="chat-new-room-btn">
              <Plus className="w-4 h-4" />
            </Button>
          )}
        </div>
        <ScrollArea className="flex-1">
          {roomsLoading ? (
            <div className="p-4 text-xs text-muted-foreground flex items-center gap-2" data-testid="chat-rooms-loading">
              <span className="inline-block w-3 h-3 rounded-full border-2 border-primary border-t-transparent animate-spin" />
              Loading chats…
            </div>
          ) : rooms.length === 0 ? (
            <div className="p-4 text-xs text-muted-foreground">
              <p>No chats visible.</p>
              {showAdminControls && (
                <p className="mt-2 text-[11px]">
                  Assign yourself to a section on the <span className="text-primary">Players</span> page
                  to see your section chats. Or join the "Musical Director" section to see all chats.
                </p>
              )}
            </div>
          ) : (
            <RoomGroupedList
              rooms={rooms}
              activeRoomId={activeRoomId}
              onPick={setActiveRoomId}
            />
          )}
        </ScrollArea>
      </aside>

      {/* Active room pane — hidden on mobile when no room selected */}
      <main
        className={`flex-1 glass-card rounded-lg flex flex-col min-h-0 ${
          activeRoomId ? "flex" : "hidden md:flex"
        }`}
        data-testid="chat-main"
      >
        {!activeRoom ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>Pick a chat from the left to start</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <header className="border-b border-white/5 p-3 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                {/* Back to rooms list — mobile only */}
                <button
                  onClick={() => { setActiveRoomId(null); setSearchParams({}, { replace: true }); }}
                  className="md:hidden p-1.5 rounded-md hover:bg-secondary/50 shrink-0"
                  title="Back to chats"
                  data-testid="chat-back-btn"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-primary shrink-0">
                  {roomIcon(activeRoom.type)}
                </div>
                <div className="min-w-0">
                  <h3 className="font-heading text-base truncate">{activeRoom.name}</h3>
                  <p className="text-[11px] text-muted-foreground capitalize">
                    {activeRoom.type} · {activeRoom.member_count} member{activeRoom.member_count === 1 ? "" : "s"}
                    {activeRoom.archived_at && " · Archived (read-only)"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setSearchOpen(true); setSearchResults([]); setSearchQ(""); }}
                  title="Search messages"
                  data-testid="chat-search-btn"
                >
                  <Search className="w-4 h-4" />
                </Button>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openRoomMembers()}
                    title="View members of this room"
                    data-testid="chat-room-members-btn"
                  >
                    <Users className="w-4 h-4" />
                  </Button>
                )}
                {isCommitteeRoom && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRatifyPanelOpen(true)}
                    title="Items for ratification"
                    data-testid="chat-ratification-btn"
                    className="relative"
                  >
                    <ClipboardList className="w-4 h-4" />
                    {messages.some((m) => m.ratification && !m.ratification.ratified_at) && (
                      <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-primary" />
                    )}
                  </Button>
                )}
                {!activeRoom.archived_at && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPollOpen(true)}
                    title="Create poll"
                    data-testid="chat-new-poll-btn"
                  >
                    <BarChart3 className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleMute}
                  title={activeRoom.muted ? "Unmute" : "Mute"}
                  data-testid="chat-mute-btn"
                >
                  {activeRoom.muted ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                </Button>
                {showAdminControls && ["custom", "event"].includes(activeRoom.type) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={deleteRoom}
                    className="text-red-400"
                    title="Delete room"
                    data-testid="chat-delete-room-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </header>

            {/* Pinned messages bar */}
            {pinnedMessages.length > 0 && (
              <div className="border-b border-white/5 bg-primary/5 px-3 py-2" data-testid="chat-pinned-bar">
                <button
                  onClick={() => setPinnedCollapsed((v) => !v)}
                  className="flex items-center gap-2 text-xs text-primary font-medium w-full text-left"
                >
                  <Pin className="w-3.5 h-3.5" />
                  {pinnedMessages.length} pinned message{pinnedMessages.length === 1 ? "" : "s"}
                  <span className="ml-auto text-muted-foreground">{pinnedCollapsed ? "▸" : "▾"}</span>
                </button>
                {!pinnedCollapsed && (
                  <div className="mt-2 space-y-1.5 max-h-40 overflow-y-auto">
                    {pinnedMessages.map((pm) => (
                      <div
                        key={pm.id}
                        className="text-xs bg-background/40 rounded-md p-2 border-l-2 border-primary/60"
                        data-testid={`chat-pinned-${pm.id}`}
                      >
                        <span className="font-medium text-primary">{pm.sender_name}:</span>{" "}
                        <span className="text-muted-foreground">{pm.body.slice(0, 140)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Messages */}
            <div
              className="flex-1 overflow-y-auto px-3 py-4 space-y-3"
              ref={scrollRef}
              data-testid="chat-messages"
            >
              {messages.length === 0 ? (
                <p className="text-center text-muted-foreground text-sm py-8">
                  No messages yet — say hi! 👋
                </p>
              ) : (
                messages.map((m) => (
                  <MessageBubble
                    key={m.id}
                    m={m}
                    mine={m.sender_id === user?.id}
                    showAdminControls={showAdminControls}
                    editingId={editingId}
                    editBody={editBody}
                    setEditBody={setEditBody}
                    onStartEdit={() => startEdit(m)}
                    onSaveEdit={saveEdit}
                    onCancelEdit={cancelEdit}
                    onDelete={() => deleteMessage(m.id)}
                    onReply={() => setReplyTo({ id: m.id, sender_name: m.sender_name, body: m.body })}
                    onReact={(emoji) => toggleReaction(m.id, emoji)}
                    onVote={(indices) => votePoll(m.id, indices)}
                    onClosePoll={() => closePoll(m.id)}
                    onPin={() => togglePin(m.id)}
                    onFlagRatify={isCommitteeRoom ? () => toggleRatifyFlag(m.id) : null}
                    currentUserId={user?.id}
                    memberNamesById={memberNamesById}
                  />
                ))
              )}
            </div>

            {/* Input */}
            {!activeRoom.archived_at && (
              <div className="border-t border-white/5 p-3 space-y-2 relative">
                {/* Reply preview */}
                {replyTo && (
                  <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-secondary/40 border-l-2 border-primary" data-testid="chat-reply-preview">
                    <Reply className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-primary font-medium">Replying to {replyTo.sender_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{replyTo.body || "(attachment)"}</p>
                    </div>
                    <button onClick={() => setReplyTo(null)} className="text-muted-foreground hover:text-foreground">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {/* Pending attachments */}
                {pendingAttachments.length > 0 && (
                  <div className="flex flex-wrap gap-2" data-testid="chat-pending-attachments">
                    {pendingAttachments.map((a) => (
                      <div key={a.id} className="flex items-center gap-2 bg-secondary/50 rounded-md px-2 py-1 text-xs">
                        {a.content_type?.startsWith("image/") ? (
                          <ImageIcon className="w-3 h-3" />
                        ) : (
                          <FileText className="w-3 h-3" />
                        )}
                        <span className="truncate max-w-[140px]">{a.filename}</span>
                        <button onClick={() => removePendingAttachment(a.id)} className="text-red-400">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {/* Mention dropdown */}
                {mentionQuery && mentionCandidates.length > 0 && (
                  <div className="absolute bottom-full left-3 mb-1 bg-popover border border-white/10 rounded-lg shadow-lg min-w-[220px] max-h-56 overflow-y-auto z-10" data-testid="chat-mention-dropdown">
                    {mentionCandidates.map((c, idx) => (
                      <button
                        key={c.id}
                        onMouseDown={(e) => { e.preventDefault(); applyMention(c); }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary/60 ${idx === mentionIndex ? "bg-secondary/60" : ""}`}
                        data-testid={`chat-mention-item-${c.id}`}
                      >
                        {c.name}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={onFileSelected}
                    className="hidden"
                    data-testid="chat-file-input"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAttachment || pendingAttachments.length >= 4}
                    title="Attach file"
                    data-testid="chat-attach-btn"
                  >
                    <Paperclip className="w-4 h-4" />
                  </Button>
                  <Input
                    value={input}
                    onChange={onInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder={`Message ${activeRoom.name}… (try @name)`}
                    maxLength={4000}
                    className="flex-1"
                    data-testid="chat-input"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={sending || (!input.trim() && pendingAttachments.length === 0)}
                    data-testid="chat-send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
            {activeRoom.archived_at && (
              <div className="border-t border-white/5 p-3 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
                <Info className="w-3 h-3" />
                This room is archived (7 days post-event) — read-only
              </div>
            )}
          </>
        )}
      </main>

      {/* Phase 3: Create poll dialog */}
      <Dialog open={pollOpen} onOpenChange={setPollOpen}>
        <DialogContent className="max-w-lg" data-testid="chat-poll-dialog">
          <DialogHeader>
            <DialogTitle>New poll</DialogTitle>
            <DialogDescription>Ask a question with 2-10 options. Everyone in this room can vote.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Question</Label>
              <Input
                value={pollQuestion}
                onChange={(e) => setPollQuestion(e.target.value)}
                placeholder="Which rehearsal works best?"
                maxLength={200}
                data-testid="poll-question"
              />
            </div>
            <div className="space-y-2">
              <Label>Options</Label>
              {pollOptions.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={opt}
                    onChange={(e) => {
                      const next = [...pollOptions];
                      next[i] = e.target.value;
                      setPollOptions(next);
                    }}
                    placeholder={`Option ${i + 1}`}
                    maxLength={120}
                    data-testid={`poll-option-input-${i}`}
                  />
                  {pollOptions.length > 2 && (
                    <button
                      onClick={() => setPollOptions(pollOptions.filter((_, idx) => idx !== i))}
                      className="text-red-400 text-sm"
                      data-testid={`poll-remove-opt-${i}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              {pollOptions.length < 10 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPollOptions([...pollOptions, ""])}
                  data-testid="poll-add-option"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add option
                </Button>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={pollMulti}
                onCheckedChange={(v) => setPollMulti(!!v)}
                data-testid="poll-multi"
              />
              Allow multiple choices per person
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPollOpen(false)}>Cancel</Button>
            <Button onClick={createPoll} data-testid="poll-create-btn">Create poll</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 3: Search messages dialog */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="max-w-lg" data-testid="chat-search-dialog">
          <DialogHeader>
            <DialogTitle>Search in {activeRoom?.name}</DialogTitle>
            <DialogDescription>Find any message in this room.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
                placeholder="Search..."
                autoFocus
                data-testid="chat-search-input"
              />
              <Button onClick={runSearch} data-testid="chat-search-run">
                <Search className="w-4 h-4" />
              </Button>
            </div>
            <div className="max-h-80 overflow-y-auto space-y-1.5" data-testid="chat-search-results">
              {searchResults.length === 0 && searchQ && (
                <p className="text-xs text-muted-foreground">No results yet — hit Enter to search.</p>
              )}
              {searchResults.map((r) => (
                <div key={r.id} className="bg-secondary/40 rounded-md p-2 text-xs">
                  <p className="text-primary font-medium">{r.sender_name}</p>
                  <p className="text-muted-foreground">{r.body}</p>
                  <p className="text-[10px] opacity-60 mt-1">{new Date(r.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ratification panel — committee chat only */}
      <Dialog open={ratifyPanelOpen} onOpenChange={setRatifyPanelOpen}>
        <DialogContent className="max-w-2xl" data-testid="chat-ratification-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gavel className="w-5 h-5 text-primary" />
              Items for ratification
            </DialogTitle>
            <DialogDescription>
              Committee chat messages that have been flagged for formal approval at a meeting.
              Download the list before each meeting, then mark each item ratified afterwards.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex rounded-lg border border-white/10 overflow-hidden">
                {[
                  { key: "pending", label: "Pending" },
                  { key: "ratified", label: "Ratified" },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setRatifyTab(tab.key)}
                    className={`px-3 py-1.5 text-xs ${
                      ratifyTab === tab.key
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary/40 text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid={`chat-ratify-tab-${tab.key}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="ml-auto flex gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadRatification("docx")}
                  data-testid="chat-ratify-download-docx"
                  className="gap-1.5"
                  disabled={ratifyItems.length === 0 && ratifyTab === "pending"}
                >
                  <FileText className="w-3.5 h-3.5" /> Word
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadRatification("xlsx")}
                  data-testid="chat-ratify-download-xlsx"
                  className="gap-1.5"
                  disabled={ratifyItems.length === 0 && ratifyTab === "pending"}
                >
                  <Download className="w-3.5 h-3.5" /> Excel
                </Button>
              </div>
            </div>
            <div
              className="max-h-[60vh] overflow-y-auto space-y-2"
              data-testid="chat-ratify-list"
            >
              {ratifyLoading && (
                <p className="text-xs text-muted-foreground">Loading…</p>
              )}
              {!ratifyLoading && ratifyItems.length === 0 && (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  {ratifyTab === "pending"
                    ? "No items waiting for ratification — flag a message in the committee chat with the gavel icon to add one."
                    : "No items have been ratified yet."}
                </p>
              )}
              {ratifyItems.map((m, idx) => {
                const r = m.ratification || {};
                return (
                  <div
                    key={m.id}
                    className="rounded-lg border border-white/10 bg-secondary/30 p-3 text-sm"
                    data-testid={`chat-ratify-item-${m.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] text-muted-foreground">
                          #{idx + 1} · Flagged by{" "}
                          <span className="text-foreground">{r.flagged_by_name}</span>
                          {r.flagged_at && ` · ${new Date(r.flagged_at).toLocaleString()}`}
                        </p>
                        <p className="text-[11px] text-muted-foreground italic mt-0.5">
                          Original: {m.sender_name}
                          {m.created_at && ` — ${new Date(m.created_at).toLocaleString()}`}
                        </p>
                        <p className="mt-1.5 break-words">{m.body}</p>
                        {r.note && (
                          <p className="mt-1.5 text-xs px-2 py-1 rounded bg-amber-500/10 border border-amber-500/20">
                            <span className="font-semibold text-amber-300">Note: </span>
                            {r.note}
                          </p>
                        )}
                        {r.ratified_at && (
                          <p className="mt-1.5 text-[11px] text-emerald-400">
                            Ratified by {r.ratified_by_name} on{" "}
                            {new Date(r.ratified_at).toLocaleDateString()}
                            {r.ratified_meeting_date && ` (meeting ${r.ratified_meeting_date})`}
                          </p>
                        )}
                      </div>
                      {ratifyTab === "pending" && showAdminControls && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => markRatified(m.id)}
                          data-testid={`chat-ratify-mark-${m.id}`}
                          className="shrink-0 gap-1"
                        >
                          <Check className="w-3.5 h-3.5" /> Mark ratified
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create custom room dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="chat-create-dialog">
          <DialogHeader>
            <DialogTitle>New custom room</DialogTitle>
            <DialogDescription>
              Pick the band members who should see and post in this room. You'll be added automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Room name</Label>
              <Input
                value={newRoomName}
                onChange={(e) => setNewRoomName(e.target.value)}
                placeholder="Tour 2026"
                data-testid="chat-new-room-name"
                maxLength={80}
              />
            </div>
            <div className="space-y-2">
              <Label>Members ({newRoomMembers.length} selected)</Label>
              <div className="max-h-64 overflow-y-auto border border-white/10 rounded-lg divide-y divide-white/5">
                {potentialMembers.map((m) => (
                  <label
                    key={m.id}
                    className="flex items-center gap-3 p-2.5 hover:bg-secondary/50 cursor-pointer"
                  >
                    <Checkbox
                      checked={newRoomMembers.includes(m.id)}
                      onCheckedChange={(v) => {
                        setNewRoomMembers((prev) =>
                          v ? [...prev, m.id] : prev.filter((x) => x !== m.id)
                        );
                      }}
                      data-testid={`chat-new-room-member-${m.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{m.name || m.email}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{m.email}</p>
                    </div>
                    {m.role === "admin" && <Badge variant="outline" className="text-[10px]">Admin</Badge>}
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">You'll be added automatically.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createRoom} data-testid="chat-create-room-save">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter-106 — admin viewer: who is in this room? */}
      <Dialog open={membersOpen} onOpenChange={setMembersOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden flex flex-col" data-testid="chat-members-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              {membersData?.room_name || activeRoom?.name || "Room"} — members
            </DialogTitle>
            <DialogDescription>
              {membersData ? (
                <>
                  {membersData.total} {membersData.total === 1 ? "person" : "people"} can see this room.
                  {membersData.room_type === "family" && (
                    <span className="block mt-1 text-xs">
                      Family rooms include everyone whose section is mapped to this family. The &ldquo;Other sections&rdquo; room is a catch-all for sections whose names don&apos;t match the standard catalogue — rename those sections (e.g. &ldquo;Solo Cornet&rdquo;, &ldquo;1st Horn&rdquo;) to merge them into the right family room.
                    </span>
                  )}
                </>
              ) : "Loading…"}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 -mx-6 px-6">
            {membersLoading && <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>}
            {!membersLoading && membersData && (membersData.total || 0) === 0 && (
              <p className="text-sm text-muted-foreground py-8 text-center">No one currently resolves to this room.</p>
            )}
            {!membersLoading && membersData && (membersData.total || 0) > 0 && (
              <div className="space-y-1.5">
                {membersData.members.map((m) => (
                  <div
                    key={m.id}
                    className="flex items-start justify-between gap-3 p-2.5 rounded-md border border-white/5 bg-background/40"
                    data-testid={`chat-room-member-${m.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm truncate">{m.name}</span>
                        {m.role === "admin" && (
                          <Badge className="text-[10px] bg-primary/20 text-primary border-primary/30">Admin</Badge>
                        )}
                        {m.is_md && (
                          <Badge variant="outline" className="text-[10px] border-blue-500/40 text-blue-300">MD</Badge>
                        )}
                        {m.is_committee && (
                          <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-300">Committee</Badge>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">{m.email}</div>
                      {m.sections && m.sections.length > 0 && (
                        <div className="text-[11px] text-muted-foreground mt-0.5 italic">
                          Sections: {m.sections.join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMembersOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      </div>
    </div>
  );
}

function RoomGroupedList({ rooms, activeRoomId, onPick }) {
  // Each family is now a single room (iter-34). Simply partition by type.
  const bandRooms = rooms.filter((r) => r.type === "band");
  const committeeRooms = rooms.filter((r) => r.type === "committee");
  const familyRooms = rooms.filter((r) => r.type === "family");
  const eventRooms = rooms.filter((r) => r.type === "event");
  const customRooms = rooms.filter((r) => r.type === "custom");

  const SectionBlock = ({ label, rooms: list }) => {
    if (!list || list.length === 0) return null;
    return (
      <div className="border-b border-white/5 last:border-b-0">
        <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold">
          {label}
        </div>
        <div className="divide-y divide-white/5">
          {list.map((r) => (
            <RoomRow
              key={r.id}
              room={r}
              active={activeRoomId === r.id}
              onClick={() => onPick(r.id)}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <SectionBlock label="General" rooms={bandRooms} />
      <SectionBlock label="Committee" rooms={committeeRooms} />
      <SectionBlock label="Sections" rooms={familyRooms} />
      <SectionBlock label="Events" rooms={eventRooms} />
      <SectionBlock label="Custom" rooms={customRooms} />
    </div>
  );
}

function RoomRow({ room: r, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 hover:bg-secondary/50 transition-colors flex items-center gap-3 ${
        active ? "bg-secondary/70" : ""
      }`}
      data-testid={`chat-room-${r.id}`}
    >
      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-primary shrink-0">
        {roomIcon(r.type)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium truncate">{r.name}</span>
          {r.unread > 0 && (
            <Badge className="bg-primary text-primary-foreground h-5 px-1.5 text-[10px]">
              {r.unread >= 99 ? "99+" : r.unread}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <span>{r.member_count} member{r.member_count === 1 ? "" : "s"}</span>
          {r.muted && <BellOff className="w-3 h-3 inline" />}
          {r.archived_at && <Lock className="w-3 h-3 inline" />}
          {r.recently_joined && r.recently_joined.length > 0 && (
            <span
              className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-full px-1.5 py-0.5 text-[9px] font-medium"
              title={`New: ${r.recently_joined.join(", ")}`}
              data-testid={`room-new-badge-${r.id}`}
            >
              +{r.recently_joined.length} new
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function MessageBubble({
  m, mine, showAdminControls, currentUserId,
  editingId, editBody, setEditBody,
  onStartEdit, onSaveEdit, onCancelEdit,
  onDelete, onReply, onReact,
  onVote, onClosePoll, onPin, onFlagRatify,
  memberNamesById = {},
}) {
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  // Which emoji's "who reacted" popover is open. null if none.
  const [openReactorEmoji, setOpenReactorEmoji] = useState(null);
  const isSystem = !m.sender_id;
  const isEditing = editingId === m.id;

  if (isSystem) {
    return (
      <div className="text-center my-3">
        <span className="text-[11px] text-muted-foreground bg-secondary/50 px-3 py-1 rounded-full">
          {m.body}
        </span>
      </div>
    );
  }

  const reactions = m.reactions || {};
  const reactionEntries = Object.entries(reactions).filter(([, users]) => users && users.length > 0);
  const attachments = m.attachments || [];
  const hasEditableWindow = mine && !m.deleted && m.created_at &&
    (Date.now() - new Date(m.created_at).getTime()) < 24 * 3600 * 1000;

  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"} group`} data-testid={`chat-msg-wrap-${m.id}`}>
      <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col gap-1`}>
        {!mine && (
          <span className="text-[11px] text-muted-foreground px-1">{m.sender_name}</span>
        )}

        {/* Reply quote */}
        {m.reply_to_preview && (
          <div className={`text-[11px] px-3 py-1.5 rounded-lg border-l-2 border-primary/60 bg-secondary/40 max-w-full ${mine ? "self-end" : "self-start"}`}>
            <span className="text-primary font-medium">{m.reply_to_preview.sender_name}</span>
            <span className="block text-muted-foreground truncate">{m.reply_to_preview.body || "(attachment)"}</span>
          </div>
        )}

        {/* Bubble */}
        <div
          className={`px-3 py-2 rounded-2xl text-sm break-words ${
            mine
              ? "bg-primary text-primary-foreground rounded-br-sm"
              : "bg-secondary/70 text-foreground rounded-bl-sm"
          } ${m.deleted ? "italic opacity-60" : ""}`}
          data-testid={`chat-message-${m.id}`}
        >
          {m.deleted ? (
            "— message deleted —"
          ) : isEditing ? (
            <div className="flex flex-col gap-2 min-w-[240px]">
              <Input
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                className="text-sm bg-background text-foreground"
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); onSaveEdit(); }
                  if (e.key === "Escape") { e.preventDefault(); onCancelEdit(); }
                }}
                data-testid={`chat-edit-input-${m.id}`}
              />
              <div className="flex gap-2 justify-end">
                <button onClick={onCancelEdit} className="text-xs opacity-80 hover:opacity-100" data-testid={`chat-edit-cancel-${m.id}`}>Cancel</button>
                <button onClick={onSaveEdit} className="text-xs font-medium" data-testid={`chat-edit-save-${m.id}`}>Save</button>
              </div>
            </div>
          ) : (
            <>
              {m.poll ? (
                <PollView poll={m.poll} messageId={m.id} mine={mine} onVote={onVote} onClose={onClosePoll} canClose={mine || showAdminControls} currentUserId={currentUserId} />
              ) : (
                <BodyWithMentions body={m.body} mentionDisplay={m.mention_display} mentions={m.mentions} currentUserId={currentUserId} />
              )}
              {attachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {attachments.map((a) => (
                    <AttachmentChip key={a.id} att={a} mine={mine} />
                  ))}
                </div>
              )}
              {m.pinned_at && (
                <div className="mt-1 text-[10px] flex items-center gap-1 opacity-70">
                  <Pin className="w-2.5 h-2.5" />
                  Pinned
                </div>
              )}
              {m.ratification && !m.ratification.ratified_at && (
                <div
                  className="mt-1 text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 w-fit"
                  data-testid={`chat-ratify-badge-pending-${m.id}`}
                >
                  <Gavel className="w-2.5 h-2.5" /> Needs ratification
                  {m.ratification.note && (
                    <span className="opacity-80 italic max-w-[180px] truncate">· {m.ratification.note}</span>
                  )}
                </div>
              )}
              {m.ratification && m.ratification.ratified_at && (
                <div
                  className="mt-1 text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 w-fit"
                  data-testid={`chat-ratify-badge-done-${m.id}`}
                >
                  <Check className="w-2.5 h-2.5" /> Ratified
                  {m.ratification.ratified_meeting_date && (
                    <span className="opacity-80">· {m.ratification.ratified_meeting_date}</span>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Reactions row */}
        {reactionEntries.length > 0 && (
          <div className={`flex flex-wrap gap-1 px-1 ${mine ? "justify-end" : "justify-start"}`}>
            {reactionEntries.map(([emoji, users]) => {
              const mineReacted = users.includes(currentUserId);
              const names = users.map((uid) => {
                if (uid === currentUserId) return "You";
                return memberNamesById[uid] || "Someone";
              });
              const tooltipText = `${names.join(", ")} reacted with ${emoji}`;
              const isOpen = openReactorEmoji === emoji;
              return (
                <div key={emoji} className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // On click: toggle the "who reacted" popover.
                      // Long-press / second click on own reaction still toggles it via picker.
                      setOpenReactorEmoji(isOpen ? null : emoji);
                    }}
                    onDoubleClick={() => onReact(emoji)}
                    title={tooltipText}
                    aria-label={tooltipText}
                    className={`text-xs px-1.5 py-0.5 rounded-full border ${mineReacted ? "bg-primary/20 border-primary/50" : "bg-secondary/40 border-white/10"}`}
                    data-testid={`chat-reaction-${m.id}-${emoji}`}
                  >
                    {emoji} {users.length}
                  </button>
                  {isOpen && (
                    <div
                      className={`absolute z-20 mt-1 ${mine ? "right-0" : "left-0"} min-w-[160px] max-w-[240px] rounded-lg border border-white/10 bg-popover text-popover-foreground shadow-lg p-2 text-xs`}
                      onMouseLeave={() => setOpenReactorEmoji(null)}
                      data-testid={`chat-reactors-${m.id}-${emoji}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-base leading-none">{emoji}</span>
                        <button
                          onClick={() => setOpenReactorEmoji(null)}
                          className="text-[10px] opacity-60 hover:opacity-100"
                          aria-label="Close"
                        >
                          ×
                        </button>
                      </div>
                      <ul className="space-y-0.5">
                        {names.map((n, idx) => (
                          <li key={`${users[idx]}-${idx}`} className="truncate">{n}</li>
                        ))}
                      </ul>
                      {mineReacted && (
                        <button
                          onClick={() => { onReact(emoji); setOpenReactorEmoji(null); }}
                          className="mt-1.5 w-full text-[10px] text-muted-foreground hover:text-foreground border-t border-white/10 pt-1"
                          data-testid={`chat-reaction-remove-${m.id}-${emoji}`}
                        >
                          Remove your reaction
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Actions row */}
        {!m.deleted && !isEditing && (
          <div className="flex items-center gap-3 px-1 relative">
            <span className="text-[10px] text-muted-foreground">
              {fmtTime(m.created_at)}
              {m.edited_at && <span className="ml-1 italic">(edited)</span>}
            </span>
            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => setShowReactionPicker((v) => !v)}
                className="text-[10px] text-muted-foreground hover:text-foreground"
                title="React"
                data-testid={`chat-react-btn-${m.id}`}
              >
                <SmilePlus className="w-3 h-3" />
              </button>
              <button
                onClick={onReply}
                className="text-[10px] text-muted-foreground hover:text-foreground"
                title="Reply"
                data-testid={`chat-reply-btn-${m.id}`}
              >
                <Reply className="w-3 h-3" />
              </button>
              {hasEditableWindow && (
                <button
                  onClick={onStartEdit}
                  className="text-[10px] text-muted-foreground hover:text-foreground"
                  title="Edit"
                  data-testid={`chat-edit-btn-${m.id}`}
                >
                  <Edit className="w-3 h-3" />
                </button>
              )}
              {showAdminControls && !m.poll && (
                <button
                  onClick={onPin}
                  className="text-[10px] text-muted-foreground hover:text-foreground"
                  title={m.pinned_at ? "Unpin" : "Pin"}
                  data-testid={`chat-pin-btn-${m.id}`}
                >
                  {m.pinned_at ? <PinOff className="w-3 h-3" /> : <Pin className="w-3 h-3" />}
                </button>
              )}
              {onFlagRatify && !m.poll && !m.deleted && (
                <button
                  onClick={onFlagRatify}
                  className={`text-[10px] hover:text-foreground ${
                    m.ratification && !m.ratification.ratified_at
                      ? "text-amber-400"
                      : "text-muted-foreground"
                  }`}
                  title={
                    m.ratification && !m.ratification.ratified_at
                      ? "Remove ratification flag"
                      : "Flag for ratification at next meeting"
                  }
                  data-testid={`chat-ratify-flag-btn-${m.id}`}
                >
                  <Gavel className="w-3 h-3" />
                </button>
              )}
              {(mine || showAdminControls) && (
                <button
                  onClick={onDelete}
                  className="text-[10px] text-red-400 hover:text-red-300"
                  title="Delete"
                  data-testid={`chat-delete-${m.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
            {showReactionPicker && (
              <div
                className="absolute bottom-full mb-1 left-0 bg-popover border border-white/10 rounded-full shadow-lg flex gap-1 p-1 z-10"
                data-testid={`chat-reaction-picker-${m.id}`}
              >
                {QUICK_REACTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => { onReact(emoji); setShowReactionPicker(false); }}
                    className="text-base hover:scale-125 transition-transform px-1"
                    data-testid={`chat-pick-reaction-${m.id}-${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PollView({ poll, messageId, mine, onVote, onClose, canClose, currentUserId }) {
  if (!poll || !poll.options) return null;
  const totalVotes = poll.options.reduce((sum, o) => sum + (o.votes?.length || 0), 0);
  const myVotes = new Set();
  poll.options.forEach((o, i) => {
    if ((o.votes || []).includes(currentUserId)) myVotes.add(i);
  });
  const closed = poll.closed || (poll.closes_at && new Date(poll.closes_at) <= new Date());

  const handleVote = (idx) => {
    if (closed) return;
    let next;
    if (poll.multi_choice) {
      next = new Set(myVotes);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
    } else {
      // Toggle: if it was my vote, unvote; else replace
      next = myVotes.has(idx) ? new Set() : new Set([idx]);
    }
    onVote([...next]);
  };

  return (
    <div className="min-w-[220px]">
      <div className="flex items-start gap-2 mb-2">
        <BarChart3 className="w-4 h-4 shrink-0 mt-0.5" />
        <p className="font-medium text-sm">{poll.question}</p>
      </div>
      <div className="space-y-1.5">
        {poll.options.map((opt, idx) => {
          const count = opt.votes?.length || 0;
          const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
          const mineVoted = myVotes.has(idx);
          return (
            <button
              key={idx}
              onClick={() => handleVote(idx)}
              disabled={closed}
              className={`w-full text-left rounded-md px-2 py-1.5 text-xs relative overflow-hidden border ${
                mineVoted
                  ? (mine ? "border-primary-foreground/60 bg-primary-foreground/10" : "border-primary/60 bg-primary/10")
                  : "border-white/10 bg-background/30"
              } ${closed ? "cursor-not-allowed opacity-70" : "hover:bg-background/50"}`}
              data-testid={`poll-option-${messageId}-${idx}`}
            >
              <div
                className={`absolute inset-y-0 left-0 ${mine ? "bg-primary-foreground/15" : "bg-primary/15"}`}
                style={{ width: `${pct}%` }}
              />
              <div className="relative flex items-center justify-between gap-2">
                <span className="font-medium">
                  {mineVoted && "✓ "}
                  {opt.label}
                </span>
                <span className="text-[10px] opacity-70 shrink-0">{count} · {pct}%</span>
              </div>
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-2 text-[10px] opacity-70">
        <span>{totalVotes} vote{totalVotes === 1 ? "" : "s"} · {poll.multi_choice ? "multi-choice" : "single-choice"}{closed && " · closed"}</span>
        {canClose && !closed && (
          <button onClick={onClose} className="underline hover:opacity-100" data-testid={`poll-close-${messageId}`}>
            Close poll
          </button>
        )}
      </div>
    </div>
  );
}

// Renders body with @mentions highlighted. Uses mention_display keys to find spans.
function BodyWithMentions({ body, mentionDisplay, mentions, currentUserId }) {
  if (!body) return null;
  const display = mentionDisplay || {};
  const keys = Object.keys(display);
  if (keys.length === 0) return <span>{body}</span>;
  // Build a flat regex that matches any of the mention tokens (quoted or bare)
  const parts = [];
  let idx = 0;
  // Scan the body for @token occurrences. Prefer longest token first.
  const sortedKeys = [...keys].sort((a, b) => b.length - a.length);
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`@(?:"(${sortedKeys.map(esc).join("|")})"|(${sortedKeys.map(esc).join("|")}))`, "g");
  let match;
  while ((match = regex.exec(body)) !== null) {
    const token = match[1] || match[2];
    if (match.index > idx) parts.push(body.slice(idx, match.index));
    const uid = display[token];
    const iMe = uid && uid === currentUserId;
    parts.push(
      <span
        key={`${match.index}`}
        // iter-128k: original style was `text-primary underline` which is
        // gold-on-gold when the sender views their own message bubble
        // (the bubble background is also `text-primary`/gold) — the tag
        // rendered invisible to the person who typed it.
        // Fix: don't override text color for mentions of OTHERS; keep
        // the currentColor and mark the token with a subtle chip +
        // underline so it's visible against any bubble background.
        // Self-mentions (someone tagged me) keep the strong yellow
        // highlight — that always renders on the receiver's neutral
        // bubble.
        className={
          iMe
            ? "font-semibold px-1 rounded bg-yellow-400/50 text-yellow-100"
            : "font-semibold underline underline-offset-2 decoration-2 decoration-current/60"
        }
      >
        @{token}
      </span>
    );
    idx = match.index + match[0].length;
  }
  if (idx < body.length) parts.push(body.slice(idx));
  return <span>{parts}</span>;
}

function AttachmentChip({ att, mine }) {
  const isImage = att.content_type?.startsWith("image/");
  const handleDownload = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    try {
      const r = await apiClient.get(`/chat/attachments/${att.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  // iter-99: render images inline in the chat thread, not as a download chip.
  if (isImage) {
    return (
      <ChatImageAttachment
        att={att}
        mine={mine}
        onDownload={handleDownload}
      />
    );
  }

  return (
    <button
      onClick={handleDownload}
      className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-md border w-full text-left ${
        mine ? "bg-primary-foreground/10 border-primary-foreground/30 hover:bg-primary-foreground/20" : "bg-background/40 border-white/10 hover:bg-background/60"
      }`}
      data-testid={`chat-attachment-${att.id}`}
    >
      <FileText className="w-3.5 h-3.5 shrink-0" />
      <span className="truncate flex-1">{att.filename}</span>
      <Download className="w-3.5 h-3.5 shrink-0 opacity-60" />
    </button>
  );
}

// Auth-aware inline image renderer. The /chat/attachments/{id} endpoint
// requires a Bearer token, so we fetch the bytes via apiClient and turn
// them into a blob URL that an <img> tag can render. Click to open the
// full-size image in a lightbox.
function ChatImageAttachment({ att, mine, onDownload }) {
  const [src, setSrc] = useState(null);
  const [errored, setErrored] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const r = await apiClient.get(`/chat/attachments/${att.id}`, { responseType: "blob" });
        if (cancelled) return;
        objectUrl = window.URL.createObjectURL(new Blob([r.data], { type: att.content_type || "image/*" }));
        setSrc(objectUrl);
      } catch {
        if (!cancelled) setErrored(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [att.id, att.content_type]);

  if (errored) {
    // Fallback to a tappable chip if the image fails to load.
    return (
      <button
        onClick={onDownload}
        className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-md border w-full text-left ${
          mine ? "bg-primary-foreground/10 border-primary-foreground/30" : "bg-background/40 border-white/10"
        }`}
        data-testid={`chat-attachment-${att.id}`}
      >
        <ImageIcon className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate flex-1">{att.filename}</span>
        <Download className="w-3.5 h-3.5 shrink-0 opacity-60" />
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => src && setLightboxOpen(true)}
        className={`block max-w-full overflow-hidden rounded-lg border ${
          mine ? "border-primary-foreground/20" : "border-white/10"
        } bg-black/20 hover:opacity-95 transition-opacity`}
        data-testid={`chat-attachment-${att.id}`}
        title={att.filename}
        aria-label={`Open image ${att.filename}`}
      >
        {src ? (
          <img
            src={src}
            alt={att.filename}
            className="block max-h-72 w-auto max-w-full object-contain"
            data-testid={`chat-attachment-image-${att.id}`}
          />
        ) : (
          <div className="flex items-center gap-2 text-xs px-3 py-6 text-muted-foreground">
            <ImageIcon className="w-4 h-4" />
            Loading image…
          </div>
        )}
      </button>
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightboxOpen(false)}
          data-testid={`chat-attachment-lightbox-${att.id}`}
          role="dialog"
          aria-modal="true"
        >
          <img
            src={src}
            alt={att.filename}
            className="max-h-[92vh] max-w-[96vw] object-contain rounded-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDownload?.(e); }}
            className="absolute top-4 right-16 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm flex items-center gap-1.5"
            data-testid={`chat-attachment-lightbox-download-${att.id}`}
          >
            <Download className="w-4 h-4" /> Download
          </button>
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </>
  );
}
